import { AppliedFilters, Button, Container, Table } from "@fluidattacks/design";
import type { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useTodEventsFilters } from "./filters";
import { useEventsQuery } from "./hooks";
import type { IEventAttr } from "./types";

import { statusFormatter } from "features/vulnerabilities/formatters/status";
import { useTable } from "hooks/use-table";
import { filterDate } from "utils/date";
import { formatDate } from "utils/date";

const tableColumns: ColumnDef<IEventAttr>[] = [
  {
    accessorFn: (row): string => row.organization,
    enableColumnFilter: false,
    header: "Organization",
    size: 100,
  },
  {
    accessorFn: (row): string => row.groupName,
    enableColumnFilter: false,
    header: "Group name",
  },
  {
    accessorFn: (row): string | undefined => row.root?.nickname,
    enableColumnFilter: false,
    header: "Root",
  },
  {
    accessorKey: "eventDate",
    cell: (cell): string => formatDate(String(cell.getValue() ?? "")),
    filterFn: filterDate,
    header: "Event date",
  },
  { accessorKey: "detail", header: "Description", size: 250 },
  { accessorKey: "eventType", header: "Type", size: 300 },
  {
    accessorKey: "eventStatus",
    cell: (cell): JSX.Element => statusFormatter(String(cell.getValue())),
    header: "Status",
  },
];

const Events: FC = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { events, loading } = useEventsQuery();
  const [search, setSearch] = useState("");
  const [filteredDataset, setFilteredDataset] = useState<IEventAttr[]>(events);
  const { Filters, options, removeFilter } = useTodEventsFilters(
    events,
    setFilteredDataset,
  );

  const goToEvent = useCallback(
    (rowInfo: Row<IEventAttr>): void => {
      navigate(
        `/groups/${rowInfo.original.groupName}/events/${rowInfo.original.id}/description`,
      );
    },
    [navigate],
  );

  const formattedVulns = useMemo(
    (): IEventAttr[] =>
      filteredDataset.filter(
        ({ detail, eventType, groupName, organization }): boolean =>
          detail.toLowerCase().includes(search.toLowerCase()) ||
          eventType.toLowerCase().includes(search.toLowerCase()) ||
          groupName.toLowerCase().includes(search.toLowerCase()) ||
          organization.toLowerCase().includes(search.toLowerCase()),
      ),
    [filteredDataset, search],
  );

  const { table } = useTable({
    columns: tableColumns,
    data: formattedVulns,
    id: "events-todo-table",
    options: {
      actionsOptions: { onSearch: setSearch },
      filtersMode: "client-side",
      loading,
      paginationMode: "client-side",
      rowOptions: { onClick: goToEvent },
    },
    total: formattedVulns.length,
  });

  return (
    <Table table={table}>
      <Table.Options>
        <Table.Controls>
          <Filters />
          <Container id={"actions-container"}>
            <Table.Export csvConfig={{ export: true }} data={formattedVulns}>
              <Button
                icon={"file-export"}
                id={"CSVExportBtn"}
                tooltip={t("buttons.exportTip", { format: "CSV" })}
                variant={"ghost"}
              >
                {t("group.findings.exportCsv.text")}
              </Button>
            </Table.Export>
          </Container>
        </Table.Controls>
      </Table.Options>
      <Table.Caption>
        <AppliedFilters onClose={removeFilter} options={options} />
      </Table.Caption>
      <Table.Content />
      <Table.Footer />
    </Table>
  );
};

export { Events };
